import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import DashboardContent from "../../components/DashboardContent";
import PatientNarrativeQuestionLabel from "../../components/PatientNarrativeQuestionLabel";
import RadioGroup from "../../components/RadioGroup";
import Loader from "../../utils/Loader";
import usePatientNarrativeWizardSection from "../../hooks/usePatientNarrativeWizardSection";
import usePatientNarrativeQuestions from "../../hooks/usePatientNarrativeQuestions";
import { usePermissions } from "../../contexts/PermissionsContext";
import { PERMISSIONS } from "../../utils/permissions";
import useModalWindow from "../../hooks/useModalWindow";
import { CsrAPI } from "../../apis/CsrAPI";
import { PATIENT_NARRATIVE_WIZARD_SECTIONS } from "../../utils/patientNarrativeWizardSections";
import {
  describeSaveError,
  describeLoadError,
} from "../../utils/patientNarrativeErrors";
import questionsData from "../../assets/json/patient_narrative_questions_data.json";

// CSR's qParticipantsName choices are Titlecase; Patient Narrative's
// terminology choices are lowercase - this is the only mapping needed to
// inherit the linked CSR's convention.
const SECTION = PATIENT_NARRATIVE_WIZARD_SECTIONS.projectConventions;

const TERMINOLOGY_FROM_CSR = {
  Participants: "participants",
  Subjects: "subjects",
  Patients: "patients",
};

const ProjectConventions = () => {
  const { studyId: patientNarrativeId } = useParams();
  const { hasPermission } = usePermissions();

  // pn:wizards:read gates the GET that pre-fills this section; writing it back
  // needs pn:wizards:update on top, so without either the Save button is
  // disabled and Next navigates without submitting.
  const canReadWizards = hasPermission(PERMISSIONS.pnWizardsRead);
  const canSaveWizards =
    canReadWizards && hasPermission(PERMISSIONS.pnWizardsUpdate);
  const {
    answers,
    loading,
    saving,
    error,
    updateFields,
    save,
    errorForField,
    revealErrors,
    hasMissingRequiredFields,
  } = usePatientNarrativeWizardSection(patientNarrativeId, SECTION, {
    canRead: canReadWizards,
    canSave: canSaveWizards,
  });
  const {
    questions,
    loading: questionsLoading,
    error: questionsError,
  } = usePatientNarrativeQuestions();
  const { modalWindow } = useModalWindow();
  const [saveError, setSaveError] = useState("");
  const [linkedCsrId, setLinkedCsrId] = useState(null);
  const [inheritedTerminology, setInheritedTerminology] = useState(null);
  const [terminologyOverride, setTerminologyOverride] = useState(false);

  // Non-blocking: only used to decide whether to show the inherited,
  // read-only terminology value - a Standalone (unlinked) narrative set
  // just gets the normal toggle, same as today.
  useEffect(() => {
    if (!patientNarrativeId) return undefined;
    let cancelled = false;
    CsrAPI.getPatientNarrative({ patientNarrativeId })
      .then(data => {
        if (!cancelled) setLinkedCsrId(data?.linked_csr_id || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [patientNarrativeId]);

  useEffect(() => {
    if (!linkedCsrId) return undefined;
    let cancelled = false;
    CsrAPI.getOneStudy({ studyId: linkedCsrId })
      .then(study => {
        if (cancelled) return;
        const csrValue = study?.wizards?.study_design?.qParticipantsName;
        setInheritedTerminology(TERMINOLOGY_FROM_CSR[csrValue] || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [linkedCsrId]);

  const handleBlindingStatusChange = async value => {
    const confirmed = await modalWindow({
      title: "Confirm treatment blinding",
      message:
        value === "unblinded"
          ? 'This narrative set will state the actual treatment each participant received (e.g. "FAB DRUG High Dose") instead of "study treatment". This applies to every narrative in the set and cannot be changed per patient. Narratives written before database unblinding or for a DSMB package must not reveal assignment.'
          : 'This narrative set will refer generically to "study treatment" rather than the actual treatment each participant received. This applies to every narrative in the set and cannot be changed per patient.',
      confirmButtonLabel: "Confirm",
    });
    if (!confirmed) return;
    updateFields({ qTreatmentBlinding: value });
  };

  // An unanswered term is filled from the linked CSR, so it is saved like any
  // other answer (generation requires one) while still reading as inherited.
  useEffect(() => {
    if (!inheritedTerminology || !answers) return;
    if (
      answers.qParticipantTerm === undefined ||
      answers.qParticipantTerm === null
    ) {
      updateFields({ qParticipantTerm: inheritedTerminology });
    }
  }, [inheritedTerminology, answers, updateFields]);

  const showInheritedTerminology =
    inheritedTerminology &&
    answers?.qParticipantTerm === inheritedTerminology &&
    !terminologyOverride;

  const handleSave = async event => {
    event?.preventDefault?.();
    setSaveError("");
    // Trying to move on is what asks for the outstanding questions to be
    // pointed out - nothing is flagged before this.
    revealErrors();
    try {
      await save();
      return { success: true };
    } catch (err) {
      setSaveError(describeSaveError(err));
      return { success: false };
    }
  };

  // A failed GET leaves no answers to edit - show why instead of the loader.
  if (loading || questionsLoading || !answers) {
    return (
      <DashboardContent
        sidebarCSR={true}
        headerTitle={"Project & Conventions"}
        pageContent={
          !loading && error ? (
            <span className={"errmsg"} aria-live="assertive">
              {describeLoadError(error)}
            </span>
          ) : (
            <Loader loading={true} />
          )
        }
      />
    );
  }

  const blindingStatusError = errorForField("qTreatmentBlinding");
  const terminologyError = errorForField("qParticipantTerm");
  const dateConventionError = errorForField("qDateFormat");

  const PageContent = (
    <div className={"questions-content pn-project-conventions"}>
      {error && (
        <span className={"errmsg"} aria-live="assertive">
          {describeLoadError(error)}
        </span>
      )}
      {questionsError && (
        <span className={"errmsg"} aria-live="assertive">
          {describeLoadError(questionsError)}
        </span>
      )}
      {saveError && (
        <span className={"errmsg"} aria-live="assertive">
          {saveError}
        </span>
      )}

      <div className={"questions-content__container"}>
        <div className={"questions-content__questionIndexContainer"}>
          <span className={"questions-content__label-index"}>1</span>
        </div>
        <div className={"questions-content__item"}>
          <PatientNarrativeQuestionLabel
            section={SECTION}
            questions={questions}
            field={"qParticipantTerm"}
          />
          {showInheritedTerminology ? (
            <div className={"pn-inherited-value"}>
              <span className={"pn-inherited-value__text"}>
                {
                  questionsData.choices.qParticipantTerm.find(
                    choice => choice.value === inheritedTerminology
                  )?.title
                }{" "}
                (inherited from the linked CSR project)
              </span>
              <button
                type={"button"}
                className={"btn btn-link pn-inherited-value__override"}
                onClick={() => setTerminologyOverride(true)}
                disabled={saving}>
                Use a different term
              </button>
            </div>
          ) : (
            <RadioGroup
              name={"qParticipantTerm"}
              value={answers.qParticipantTerm}
              onChange={value => updateFields({ qParticipantTerm: value })}
              options={questionsData.choices.qParticipantTerm}
              disabled={saving}
            />
          )}
          {!showInheritedTerminology && terminologyError && (
            <div className={"completion-error"}>{terminologyError.message}</div>
          )}
        </div>
      </div>

      <div className={"questions-content__container"}>
        <div className={"questions-content__questionIndexContainer"}>
          <span className={"questions-content__label-index"}>2</span>
        </div>
        <div className={"questions-content__item"}>
          <PatientNarrativeQuestionLabel
            section={SECTION}
            questions={questions}
            field={"qTreatmentBlinding"}
          />
          <RadioGroup
            name={"qTreatmentBlinding"}
            value={answers.qTreatmentBlinding}
            onChange={handleBlindingStatusChange}
            options={questionsData.choices.qTreatmentBlinding}
            disabled={saving}
          />
          {blindingStatusError && (
            <div className={"completion-error"}>
              {blindingStatusError.message}
            </div>
          )}
        </div>
      </div>

      <div className={"questions-content__container"}>
        <div className={"questions-content__questionIndexContainer"}>
          <span className={"questions-content__label-index"}>3</span>
        </div>
        <div className={"questions-content__item"}>
          <PatientNarrativeQuestionLabel
            section={SECTION}
            questions={questions}
            field={"qDateFormat"}
          />
          <RadioGroup
            name={"qDateFormat"}
            value={answers.qDateFormat}
            onChange={value => updateFields({ qDateFormat: value })}
            options={questionsData.choices.qDateFormat}
            disabled={saving}
          />
          {dateConventionError && (
            <div className={"completion-error"}>
              {dateConventionError.message}
            </div>
          )}
        </div>
      </div>

      <div className={"questions-content__container"}>
        <div className={"questions-content__questionIndexContainer"}>
          <span className={"questions-content__label-index"}>4</span>
        </div>
        <div className={"questions-content__item"}>
          <PatientNarrativeQuestionLabel
            section={SECTION}
            questions={questions}
            field={"qCiomsFollowUpHandling"}
          />
          <RadioGroup
            name={"qCiomsFollowUpHandling"}
            value={answers.qCiomsFollowUpHandling}
            onChange={value => updateFields({ qCiomsFollowUpHandling: value })}
            options={questionsData.choices.qCiomsFollowUpHandling}
            disabled={saving}
          />
        </div>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={true}
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={"Project & Conventions"}
      pageContent={PageContent}
      prevButton={{
        buttonTitle: "File Upload",
        buttonGoToAction: "/pn/" + patientNarrativeId + "/file-upload",
      }}
      nextButton={{
        buttonTitle: "Patient Scope",
        buttonGoToAction: "/pn/" + patientNarrativeId + "/patient-scope",
        disabled: hasMissingRequiredFields,
      }}
      saveButton={true}
      savePermission={[PERMISSIONS.pnWizardsRead, PERMISSIONS.pnWizardsUpdate]}
      saveButtonHandler={handleSave}
    />
  );
};

export default ProjectConventions;
